/**
 * 移入批次抽屉：执行结果表（转写自原型 moveRows）。
 */
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MoveInResult, MoveInResultRow } from '../types';

const RESULT_COLORS: Record<MoveInResult, string> = {
    MOVED_IN: '#0d7a3d',
    MOVED_IN_REPRINT: '#c26b00',
    MOVED_IN_NEW_BAG_PENDING_PRINT: '#c26b00',
    MOVED_IN_NEW_BAG_CREATED: '#c26b00',
    FAILED: '#f5222d',
};

type ResultRow = MoveInResultRow & { key: string };

export const MoveInResultTable: React.FC<{ rows: MoveInResultRow[] }> = ({ rows }) => {
    const { t } = useTranslation();
    const none = t('wms.sortingBatch.common.none');

    const bagText = (r: MoveInResultRow): string => {
        if (r.result === 'FAILED') return none ?? '';
        if (!r.bag) return t('wms.sortingBatch.moveIn.newBag') ?? '';
        return r.bagIsNew ? `${r.bag}${t('wms.sortingBatch.moveIn.newBagSuffix')}` : r.bag;
    };

    const resultText = (r: MoveInResultRow): string => {
        if (r.result === 'FAILED') return r.reason ? t(`wms.sortingBatch.moveIn.reason.${r.reason}`) ?? '' : none ?? '';
        const text = t(`wms.sortingBatch.moveIn.result.${r.result}`) ?? '';
        // 后端同步执行时逐单失败的单数：提示重新提交该包牌
        return r.failedOrderCount ? `${text} · ${t('wms.sortingBatch.moveIn.failedOrders', { count: r.failedOrderCount })}` : text;
    };

    const columns: ColumnsType<ResultRow> = [
        {
            title: t('wms.sortingBatch.moveIn.columns.number'),
            dataIndex: 'number',
            align: 'center',
            render: (v: string) => <span className="font-mono">{v}</span>,
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.bag'),
            key: 'bag',
            align: 'center',
            render: (_: unknown, r) => bagText(r),
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.targetBatch'),
            key: 'batchNumber',
            align: 'center',
            render: (_: unknown, r) => <span className="font-mono">{r.batchNumber || none}</span>,
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.result'),
            key: 'result',
            align: 'center',
            render: (_: unknown, r) => <span style={{ color: RESULT_COLORS[r.result] }}>{resultText(r)}</span>,
        },
    ];

    const data: ResultRow[] = rows.map((r, i) => ({ ...r, key: `${i}-${r.number}` }));
    return <Table<ResultRow> size="small" bordered columns={columns} dataSource={data} pagination={false} />;
};
